/* eslint-disable @typescript-eslint/no-shadow */
import React, { useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { KDialog } from "@kyburz/kdesign-system";
import { useDispatch } from "react-redux";
import useSetState from "../../hooks/useSetState";
import { useTableFilters } from "../../hooks/useTableFilters";
import { useTableSorts } from "../../hooks/useTableSorts";
import KInfiniteTable from "../../components/table/KInfiniteTable";
import { useStyles } from "./styles";
import { ScheduledUpdatesColumns } from "./types";
import { SchedulePopupData, UpdateType } from "../devices/DevicesList/types";
import { transformFilterToId, transformIdToFilter, transformIdToSort } from "./utils";
import { getColumns } from "./columns";
import {
  deleteScheduledFWUpdate,
  getScheduledFWUpdateByID,
  getScheduledFWUpdates
} from "../../apiCalls/config/scheduledFWUpdates";
import {
  deleteScheduledConfigUpdate,
  getScheduledConfigUpdateByID,
  getScheduledConfigUpdates
} from "../../apiCalls/config/scheduledConfigUpdates";
import { setAlert } from "../../redux/actions/actions";
import { formatDateTZ } from "../../utils/timeUtils";
import ScheduledUpdatePopup from "../devices/DevicesList/ScheduledUpdatePopup/ScheduledUpdatePopup";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      filterTable={props.filterTable}
      loading={props.loading}
      sortTable={props.sortTable}
      manualRowSelectedKey={props.manualRowSelectedKey}
      onControlledSelect={props.onControlledSelect}
      onControlledSelectAll={props.onControlledSelectAll}
      autoResetSelectedRows={props.autoResetSelectedRows}
      hasFilter
      manualFilters
      hasStickyColumns={true}
      manualSortBy
      filterValue={props.filterValue}
      initialSortBy={props.initialSortBy}
      selectionColumnProps={{ sticky: "left" }}
    />
  ),
  (oldProps, newProps) => {
    return (
      oldProps.loading === newProps.loading && oldProps.data === newProps.data
    );
  },
);

const initialSortRule = { id: ScheduledUpdatesColumns.Created, desc: false };

const ScheduledUpdates: React.FC = () => {
  const classes = useStyles();
  const { CancelToken } = axios;
  const dispatch = useDispatch();

  const [filters, updateFilters, filterBy] = useTableFilters(
    ScheduledUpdatesColumns,
    transformIdToFilter,
    transformFilterToId,
  );

  const [sorts, updateSorts] = useTableSorts(initialSortRule);

  const sortBy = useMemo(() => {
    return sorts.map((x) => `${transformIdToSort(x.id, x.desc)}`).join(",");
  }, [sorts]);

  const [
    {
      loading,
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      cancelTokenSource,
      reload,
      scheduledUpdateToDelete,
      scheduledUpdateToDeleteType,
      deleteModalOpen,
      schedulePopupData,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    reload: false,
    isNextPageLoading: false,
    items: [],
    loading: false,
    cancelTokenSource: null,
    deleteModalOpen: false,
    scheduledUpdateToDelete: "",
    scheduledUpdateToDeleteType: "" as string,
    schedulePopupData: null as SchedulePopupData | null,
  });

  const getScheduleUpdateByIDImeis = (scheduledUpdate: any) => {
    const scheduledUpdateIDAux = scheduledUpdate.id;
    const { imeis, deviceFirmwareID, updateType } = scheduledUpdate;
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();
    const fetchFn = updateType === UpdateType.config
      ? getScheduledConfigUpdateByID(scheduledUpdateIDAux, source.token)
      : getScheduledFWUpdateByID(scheduledUpdateIDAux, source.token);
    fetchFn
      .then((res) => {
        const devicesData = res.devices_data;
        const hardwareTypesAux = new Set(
          Object.values(devicesData).map((d: any) => d?.hardware_type?.toLowerCase())
        );
        const simTypesAux = new Set(
          Object.values(devicesData).map((d: any) => d?.sim_type?.toLowerCase())
        );
        setState({
          schedulePopupData: {
            scheduledUpdateID: scheduledUpdateIDAux,
            deviceIds: imeis,
            updateType,
            simTypes: simTypesAux,
            hardwareTypes: hardwareTypesAux,
            deviceFirmwareID,
            allowedFrom: scheduledUpdate.allowedFrom,
            allowedTo: scheduledUpdate.allowedTo,
            ignitionOn: scheduledUpdate.onlyIgnitionOn === "Yes",
            ignitionOff: scheduledUpdate.onlyIgnitionOff === "Yes",
          },
        });
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const loadScheduledUpdatesPage = useCallback(
    (query, page, startEmpty = true) => {
      if (cancelTokenSource !== null) {
        cancelTokenSource.cancel("Search cancelled");
      }
      const source = CancelToken.source();
      setState({
        isNextPageLoading: true,
        cancelTokenSource: source,
        loading: true,
      });
      const mapItem = (updateType: UpdateType) => (x) => ({
        id: x.id,
        updateType,
        createdAt: x.created,
        created: formatDateTZ(x.created),
        modifiedAt: x.modified,
        modified: formatDateTZ(x.modified),
        deviceFirmwareID: updateType === UpdateType.firmware ? x.device_firmware_id : x.device_config_id,
        allowedFrom: x.allowed_from,
        allowedTo: x.allowed_to,
        allowedFromStr: formatDateTZ(x.allowed_from),
        allowedToStr: formatDateTZ(x.allowed_to),
        onlyIgnitionOn: x.only_ignition_on ? "Yes" : "No",
        onlyIgnitionOff: x.only_ignition_off ? "Yes" : "No",
        imeis: x.imeis,
        imeisStr: x.imeis.join(", "),
        createdByUserID: x.created_by.user_id,
        createdBy: `${x.created_by.first_name} ${x.created_by.last_name}`,
        createdByUserAvatar: x.created_by.avatar,
        status: x.status,
        executeJobID: x.execute_job_id,
        expireJobID: x.expire_job_id,
        history: x.history,
        historyStr: x.history.length
          ? x.history.map((h) => `${h.action}`).join(" -> ")
          : "—",
        allowedActions: x.allowed_actions.join(", "),
      });

      Promise.all([
        getScheduledFWUpdates(query, page, source.token, filterBy, sortBy),
        getScheduledConfigUpdates(query, page, source.token, filterBy, sortBy),
      ])
        .then(([fwRes, configRes]) => {
          const previousArray = startEmpty ? [] : items;
          const fwItems = fwRes.data?.data.map(mapItem(UpdateType.firmware)) ?? [];
          const configItems = configRes.data?.data.map(mapItem(UpdateType.config)) ?? [];
          const allItems = [...previousArray, ...fwItems, ...configItems];
          const primarySort = sorts[0];
          if (primarySort) {
            const rawField = primarySort.id === ScheduledUpdatesColumns.Created
              ? "createdAt"
              : primarySort.id === ScheduledUpdatesColumns.Modified
                ? "modifiedAt"
                : primarySort.id;
            allItems.sort((a, b) => {
              const aVal = a[rawField] ?? "";
              const bVal = b[rawField] ?? "";
              const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
              return primarySort.desc ? -cmp : cmp;
            });
          }
          setState({
            items: allItems,
            isNextPageLoading: false,
            pagesLoaded: page,
            cancelToken: null,
            loading: false,
          });
        })
        .catch((e) => {
          setState({
            isNextPageLoading: false,
            pagesLoaded: page,
            cancelToken: null,
          });
          console.error(e);
        });
    },
    [filterBy, sortBy, items, sorts],
  );

  const loadNextPage = useCallback(() => {
    if (hasNextPage && items.length > 0) {
      loadScheduledUpdatesPage(query, pagesLoaded + 1, false);
    }
  }, [items, query, pagesLoaded, hasNextPage]);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadScheduledUpdatesPage(query, 1, true);
    }
  }, [reload]);

  useEffect(() => {
    loadScheduledUpdatesPage(query, 1, true);
  }, [query, filterBy, sortBy]);

  const handleReload = () => {
    setState({ reload: true });
  };

  // DELETE
  const handleDelete = async () => {
    setState({ deleteModalOpen: false, scheduledUpdateToDelete: "", scheduledUpdateToDeleteType: "", query: "" });
    const deleteFn = scheduledUpdateToDeleteType === UpdateType.config
      ? deleteScheduledConfigUpdate(scheduledUpdateToDelete)
      : deleteScheduledFWUpdate(scheduledUpdateToDelete);
    const res = await deleteFn;
    if (res.data) {
      dispatch(setAlert(`Scheduled update ${scheduledUpdateToDelete} deleted`, "success"));
      handleReload();
    } else {
      dispatch(setAlert(res.detail, "error"));
    }
  };

  const toggleDeletePopup = (e, row) => {
    setState({deleteModalOpen: !deleteModalOpen, scheduledUpdateToDelete: row.id});
  }

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={(e) => toggleDeletePopup(e, "")}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the scheduled update <b>{scheduledUpdateToDelete}</b>?
      </p>
    </KDialog>
  )

  // EDIT
  const toggleSchedulePopup = (scheduledUpdate: any) => {
    if (!schedulePopupData) {
      getScheduleUpdateByIDImeis(scheduledUpdate);
    } else {
      setState({ schedulePopupData: null });
    }
  };

  const columns = useMemo(
    () =>
      getColumns({
        classes,
        toggleSchedulePopup,
        onDeleteClick: (row) =>
          setState({ deleteModalOpen: true, scheduledUpdateToDelete: row.id, scheduledUpdateToDeleteType: row.updateType }),
      }),
    [classes, toggleSchedulePopup],
  );

  const onSuccess = () => {
    setState({ reload: true, loading: false });
  };

  const startLoading = () => {
    setState({ loading: true });
  };

  return (
    <div className={classes.container}>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          data={items}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columns={columns}
          filterTable={updateFilters}
          loading={loading}
          initialSortBy={sorts}
          sortTable={updateSorts}
          manualRowSelectedKey="selected"
          autoResetSelectedRows={true}
          filterValue={filters}
        />
      </div>
      {deletePopup}
      {schedulePopupData && (
        <ScheduledUpdatePopup
          data={{ ...schedulePopupData, updateType: schedulePopupData.updateType || UpdateType.firmware }}
          toggleSchedulePopup={toggleSchedulePopup}
          onSuccess={onSuccess}
          startLoading={startLoading}
        />
      )}
    </div>
  );
};

export default ScheduledUpdates;
